import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './StudentEventsView.css';

function StudentEventsView({ student, schoolId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setEvents([]);

      if (!schoolId) {
        setLoading(false);
        return;
      }

      // Load all events for the school
      const eventsRef = collection(db, 'events');
      const eventsQuery = query(eventsRef, where('schoolId', '==', schoolId));
      const snapshot = await getDocs(eventsQuery);
      const allEvents = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter events relevant to this student:
      // - Events where the student's class is in participatingClassIds
      // - Events with no participatingClassIds (school-wide events)
      const studentClassId = student.classId;
      const relevantEvents = allEvents.filter(event => {
        const participatingClassIds = event.participatingClassIds;
        // If no participatingClassIds or empty array, it's a school-wide event
        if (!participatingClassIds || !Array.isArray(participatingClassIds) || participatingClassIds.length === 0) {
          return true;
        }
        // Otherwise, check if student's class is included
        return studentClassId && participatingClassIds.includes(studentClassId);
      });

      // Sort by date (newest first)
      relevantEvents.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEvents(relevantEvents);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  }, [schoolId, student.classId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const now = new Date();

  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate >= now;
  });

  const pastEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate < now;
  });

  // Sort upcoming by date ascending (soonest first)
  upcomingEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

  // Sort past by date descending (most recent first)
  pastEvents.sort((a, b) => new Date(b.date) - new Date(a.date));

  const eventTypeIcons = {
    'Holiday': '🏖️',
    'Exam': '📝',
    'Meeting': '👥',
    'Sports': '⚽',
    'Cultural': '🎭',
    'Field Trip': '🚌',
    'Workshop': '🔧',
    'Assembly': '🏫',
    'Other': '📌',
  };

  const getEventIcon = (type) => {
    if (!type) return '📌';
    return eventTypeIcons[type] || '📌';
  };

  const renderEventCard = (event) => {
    const eventDate = new Date(event.date);
    return (
      <div key={event.id} className="event-card">
        <div className="event-card-header">
          <span className="event-icon">{getEventIcon(event.type)}</span>
          <h3>{event.title}</h3>
          {event.type && <span className="event-type-badge">{event.type}</span>}
        </div>
        {event.description && (
          <p className="event-description">{event.description}</p>
        )}
        <div className="event-meta">
          <span className="event-date">📅 {eventDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
          {event.location && typeof event.location === 'object' && event.location.address && <span className="event-location">📍 {event.location.address}</span>}
          {event.location && typeof event.location === 'string' && <span className="event-location">📍 {event.location}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="student-events-view">
      <h2>Events</h2>

      {loading ? (
        <div className="loading">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎉</div>
          <h3>No Events</h3>
          <p>Events for your school will appear here.</p>
        </div>
      ) : (
        <>
          <div className="events-section">
            <h3 className="section-title">Upcoming Events</h3>
            {upcomingEvents.length === 0 ? (
              <div className="no-events-subsection">
                <p>No upcoming events.</p>
              </div>
            ) : (
              <div className="events-list">
                {upcomingEvents.map(renderEventCard)}
              </div>
            )}
          </div>

          <div className="events-section">
            <h3 className="section-title">Past Events</h3>
            {pastEvents.length === 0 ? (
              <div className="no-events-subsection">
                <p>No past events.</p>
              </div>
            ) : (
              <div className="events-list">
                {pastEvents.map(renderEventCard)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default StudentEventsView;
